import { useApp } from '../contexts/AppContext.js';
import { useOrganization } from '../contexts/OrganizationContext.js';
import axios from 'axios';
import { useMemo } from 'react';

/** Custom error class for API errors with optional validation details */
class ApiError extends Error {
    constructor(message, details) {
        super(message);
        this.name = 'ApiError';
        this.details = details;
    }
}
/**
 * HTTP client for making authenticated API requests.
 * Uses a pluggable getToken function for authentication.
 */
class ApiClient {
    /**
     * @param getToken - Async function that returns an auth token
     * @param organizationId - Optional organization ID for scoped requests
     * @param getApiUrl - Function that returns the API base URL
     */
    constructor(getToken, organizationId, getApiUrl) {
        this.getToken = getToken;
        this.organizationId = organizationId;
        this.getApiUrl = getApiUrl;
    }
    async makeRequest(method, endpoint, data, config) {
        const baseURL = this.getApiUrl?.();
        if (!baseURL) {
            throw new Error('API base URL is not defined');
        }
        const url = `${baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
        const headers = {
            ...config?.headers,
            'Content-Type': 'application/json',
        };
        // Add auth token
        if (this.getToken) {
            const token = await this.getToken();
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
        }
        try {
            const response = await axios({
                method,
                url,
                data,
                headers,
                ...config,
            });
            return response.data;
        }
        catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.response) {
                    const responseData = error.response?.data;
                    if (responseData?.error) {
                        throw new ApiError(responseData.error, responseData.details);
                    }
                    throw new ApiError(`Unknown error: ${error.response.status} ${error.response.statusText}`);
                }
                else if (error.request) {
                    throw new ApiError('Network error: No response from server');
                }
            }
            throw new ApiError(`Request error: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /** @param endpoint - API endpoint path */
    async get(endpoint, config) {
        return this.makeRequest('get', endpoint, undefined, config);
    }
    /** @param endpoint - API endpoint path */
    async post(endpoint, data, config) {
        return this.makeRequest('post', endpoint, data, config);
    }
    /** @param endpoint - API endpoint path */
    async put(endpoint, data, config) {
        return this.makeRequest('put', endpoint, data, config);
    }
    /** @param endpoint - API endpoint path */
    async patch(endpoint, data, config) {
        return this.makeRequest('patch', endpoint, data, config);
    }
    /** @param endpoint - API endpoint path */
    async delete(endpoint, config) {
        return this.makeRequest('delete', endpoint, undefined, config);
    }
}
/**
 * Hook that creates a memoized ApiClient using the current auth and organization context.
 * @returns An authenticated ApiClient instance
 */
function useApiClient() {
    const { organization } = useOrganization();
    const { auth, getApiUrl } = useApp();
    const apiClient = useMemo(() => {
        const getToken = auth?.getToken ?? (() => Promise.resolve(null));
        return new ApiClient(getToken, organization?.id, getApiUrl);
    }, [auth?.getToken, organization?.id, getApiUrl]);
    return apiClient;
}

export { ApiClient, ApiError, useApiClient };
//# sourceMappingURL=api-client.js.map
